/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Build an SMS", description = "Write an SMS message, add a shortened tracking link, and check its length before you send.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Build an SMS",
        description: "Write an SMS message, add a shortened tracking link, and check its length before you send.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Create a message",
          "depth": 0,
          "id": "create-a-message"
        }, {
          "value": "Add a link",
          "depth": 0,
          "id": "add-a-link"
        }, {
          "value": "Check the length",
          "depth": 0,
          "id": "check-the-length"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      ul: "ul",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "An SMS channel and a sender identity, set up with your Hightouch team"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "SMS content is a message body and, optionally, one shortened link. Write the message, add the link, and save it."
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-message",
        children: "Create a message"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Native Delivery → Campaigns"
          }), " and select ", _jsx(_components.strong, {
            children: "Add campaign → SMS"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter your text in ", _jsx(_components.strong, {
            children: "Message"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Put any link in the ", _jsx(_components.strong, {
            children: "Link"
          }), " field."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Save"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The message is the text you type. SMS has no templates or content blocks."
      }), "\n", _jsx(_components.h2, {
        id: "add-a-link",
        children: "Add a link"
      }), "\n", _jsxs(_components.p, {
        children: ["To use the link shortener, put your URL in the ", _jsx(_components.strong, {
          children: "Link"
        }), " field. Hightouch shortens it, adds your tracking parameters, and counts the clicks."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["A URL typed into the message body is sent as you wrote it, without shortening or click tracking. Use the ", _jsx(_components.strong, {
            children: "Link"
          }), " field when you want clicks counted."]
        })
      }), "\n", _jsx(_components.p, {
        children: "The link goes at the end of the message, after your text. A message carries one link."
      }), "\n", _jsx(_components.h2, {
        id: "check-the-length",
        children: "Check the length"
      }), "\n", _jsx(_components.p, {
        children: "The editor shows a character count beneath the preview, covering your message text plus the shortened link."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Carriers send longer messages in multiple parts, and each part is billed separately."
        })
      }), "\n", _jsx(_components.p, {
        children: "Emoji and accented characters take up more room than plain text."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/personalization",
            children: "Personalize a message"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/preview-and-test",
            children: "Preview and test"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
